/* ----------------------------------------------------------------------------
 * Lead import — pure MODEL (dependency-free; unit-tested under
 * `node --test --experimental-strip-types`, like capture-model.ts).
 *
 * Owns the wire shapes of POST /v1/outreach/import[/inspect], the wizard step
 * machine, mapping helpers (preset / override / validation), the file->base64
 * envelope builder, and the human wording of outcomes + reasons. The network
 * edge lives in the sibling lead-import.ts.
 * -------------------------------------------------------------------------- */

export type ImportField =
  | 'import_row_ref' | 'business_name' | 'modality' | 'city_region' | 'address' | 'postcode' | 'website'
  | 'business_email' | 'phone' | 'practitioner_name' | 'decision_maker' | 'route_day' | 'priority'
  | 'booking_platform' | 'verification' | 'platform_evidence_url' | 'platform_evidence_note'
  | 'link_maps' | 'link_search' | 'link_source' | 'link_listings' | 'visit_approach' | 'status'
  | 'visit_date' | 'outcome_note' | 'next_action_date' | 'subscribed' | 'content_shoot_booked'
  | 'fit_notes' | 'ignore';

export interface FieldInfo { label: string; existing: boolean }

export interface SheetHeader { col: string; label: string; samples: string[]; has_links: boolean }
export interface SheetInfo { name: string; header_row: number; data_rows: number; headers: SheetHeader[] }

/** POST /v1/outreach/import/inspect response. */
export interface InspectResponse {
  file_name: string;
  sheets: SheetInfo[];
  suggested_sheet: string | null;
  suggested_mapping: Record<string, ImportField>;
  preset: { key: string; label: string; workbook_key: string; applies: boolean };
  fields: Record<ImportField, FieldInfo>;
  visit_approach_options: Record<string, string>;
  status_options: Record<string, string>;
}

export type ImportOutcome = 'imported' | 'merged' | 'skipped' | 'failed';

export interface ImportRowResult {
  row: number;
  row_ref: string | null;
  business_name: string | null;
  outcome: ImportOutcome;
  reasons: string[];
  warnings: string[];
  matched_by: string | null;
  lead_ref: string | null;
  filled?: string[];
  branch_of?: string | null;
  branch_of_row?: number;
  other_project_id?: number;
  duplicate_of_row?: number;
  preview: Record<string, string | null>;
}

export interface ImportSummary {
  rows_found: number; valid: number; imported: number; merged: number; skipped: number; failed: number;
}

/** POST /v1/outreach/import response (dry_run true = preview). */
export interface ImportResponse {
  dry_run: boolean;
  sheet: string;
  header_row: number;
  project_id: number;
  project_host?: string;
  campaign: string | null;
  workbook_key: string;
  import_batch_id: string | null;
  mapped_fields: ImportField[];
  summary: ImportSummary;
  reasons: Record<string, number>;
  results: ImportRowResult[];
}

export interface ImportRequest {
  file_name: string;
  file_b64: string;
  sheet: string;
  header_row?: number;
  mapping: Record<string, ImportField>;
  project_id: number;
  campaign?: string;
  source_directory?: string;
  workbook_key?: string;
  dry_run: boolean;
}

/** Defaults the owner chose for the Week-1 prospects (preflight 16:01 UTC). */
export const DEFAULT_CAMPAIGN = 'booksesh-partners-week1-2026-10';
export const DEFAULT_SOURCE_DIRECTORY = 'BookSesh Partners (booking software) prospects';

export type ImportStep = 'upload' | 'sheet' | 'mapping' | 'preview' | 'result';
export const IMPORT_STEPS: Array<{ key: ImportStep; label: string }> = [
  { key: 'upload', label: 'Upload' },
  { key: 'sheet', label: 'Sheet' },
  { key: 'mapping', label: 'Columns' },
  { key: 'preview', label: 'Preview' },
  { key: 'result', label: 'Result' },
];

export function stepIndex(step: ImportStep): number {
  return IMPORT_STEPS.findIndex((s) => s.key === step);
}

export const ACCEPTED_EXTENSIONS = ['.xlsx'];
export const MAX_FILE_BYTES = 3 * 1024 * 1024;

/** Client-side file gate: .xlsx only, <= 3 MB. Returns a human message or null when OK. */
export function fileProblem(name: string, size: number): string | null {
  const lower = name.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
    return 'Please choose an Excel workbook (.xlsx). CSV and .xls are not supported.';
  }
  if (size > MAX_FILE_BYTES) return 'That workbook is larger than 3 MB.';
  if (size === 0) return 'That file is empty.';
  return null;
}

/** ArrayBuffer -> base64 (chunked so large files do not blow the call stack). */
export function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

/** Pick the sheet to propose: the server suggestion, else the one with most data rows. */
export function pickSheet(inspect: Pick<InspectResponse, 'sheets' | 'suggested_sheet'>): string | null {
  if (inspect.suggested_sheet) return inspect.suggested_sheet;
  const best = [...inspect.sheets].sort((a, b) => b.data_rows - a.data_rows)[0];
  return best?.name ?? null;
}

/** Headers of the chosen sheet, in sheet order. */
export function sheetHeaders(inspect: Pick<InspectResponse, 'sheets'>, sheet: string | null): SheetHeader[] {
  return inspect.sheets.find((s) => s.name === sheet)?.headers ?? [];
}

/** Initial mapping for a sheet: the server's suggestion for known headers, 'ignore' otherwise. */
export function initialMapping(headers: SheetHeader[], suggested: Record<string, ImportField>): Record<string, ImportField> {
  const out: Record<string, ImportField> = {};
  for (const h of headers) out[h.label] = suggested[h.label] ?? 'ignore';
  return out;
}

/** Fields that may be chosen once only (everything except the ignore sink). */
export function duplicateTargets(mapping: Record<string, ImportField>): ImportField[] {
  const seen = new Map<ImportField, number>();
  for (const f of Object.values(mapping)) if (f !== 'ignore') seen.set(f, (seen.get(f) ?? 0) + 1);
  return [...seen.entries()].filter(([, n]) => n > 1).map(([f]) => f);
}

/** Mapping gate before preview: business_name mapped; no field mapped twice. */
export function mappingProblems(mapping: Record<string, ImportField>): string[] {
  const problems: string[] = [];
  if (!Object.values(mapping).includes('business_name')) problems.push('Map one column to "Business name" — it is the only required field.');
  for (const f of duplicateTargets(mapping)) problems.push(`"${f}" is mapped from more than one column — each field takes one column.`);
  return problems;
}

/** Count of columns that will be imported (not ignored). */
export function mappedCount(mapping: Record<string, ImportField>): number {
  return Object.values(mapping).filter((f) => f !== 'ignore').length;
}

export function buildImportRequest(args: {
  fileName: string; fileB64: string; sheet: string; headerRow?: number; mapping: Record<string, ImportField>;
  projectId: number; campaign: string; sourceDirectory: string; workbookKey: string; dryRun: boolean;
}): ImportRequest {
  return {
    file_name: args.fileName,
    file_b64: args.fileB64,
    sheet: args.sheet,
    ...(args.headerRow ? { header_row: args.headerRow } : {}),
    mapping: args.mapping,
    project_id: args.projectId,
    ...(args.campaign.trim() ? { campaign: args.campaign.trim() } : {}),
    ...(args.sourceDirectory.trim() ? { source_directory: args.sourceDirectory.trim() } : {}),
    ...(args.workbookKey.trim() ? { workbook_key: args.workbookKey.trim() } : {}),
    dry_run: args.dryRun,
  };
}

/** Human wording for per-row reasons/warnings (server codes are stable). */
export const REASON_TEXT: Record<string, string> = {
  business_name_missing: 'No business name on this row',
  no_identity: 'Nothing identifies this row (no name, email, website or phone)',
  duplicate_in_file: 'Same business appears earlier in this file (same postcode)',
  exists_in_other_project: 'Already tracked under a different domain — left untouched',
  unchanged: 'Already imported — nothing new to fill',
  suppressed: 'Email is on the suppression list — never stored',
  insert_failed: 'Database insert failed',
  invalid_email_kept_in_notes: 'Email looks invalid — kept in notes, not as the contact email',
  website_unparsed: 'Website text was not a URL — kept in notes',
  phone_unparsed: 'Phone text had too few digits — kept in notes',
  visit_approach_unmapped: 'Approach wording not recognised — kept in notes',
  status_unmapped: 'Sales status wording not recognised — kept in notes; status set to Not visited',
  visit_date_unparsed: 'Visit date not recognised — kept in notes',
  next_action_date_unparsed: 'Follow-up date not recognised — kept in notes',
  subscribed_unparsed: 'Subscribed? value not yes/no — kept in notes',
  content_shoot_booked_unparsed: 'Content shoot value not yes/no — kept in notes',
  no_row_id: 'No workbook ID on this row — re-uploads match by email, website or name + postcode instead',
  branch_shared_contact: 'Shares email/website with another branch — imported separately; shared contact kept in notes',
};

export function reasonText(code: string): string {
  return REASON_TEXT[code] ?? code.replace(/_/g, ' ');
}

export function outcomeLabel(outcome: ImportOutcome, dryRun: boolean): string {
  const map: Record<ImportOutcome, [string, string]> = {
    imported: ['Would import', 'Imported'],
    merged: ['Would merge', 'Merged'],
    skipped: ['Would skip', 'Skipped'],
    failed: ['Failed', 'Failed'],
  };
  return map[outcome][dryRun ? 0 : 1];
}

/** One-line summary sentence for the result banner. */
export function describeImportSummary(s: ImportSummary, dryRun: boolean): string {
  const v = dryRun ? 'would be imported' : 'imported';
  const m = dryRun ? 'would merge into existing leads' : 'merged into existing leads';
  return `${s.rows_found} rows found · ${s.valid} valid · ${s.imported} ${v} · ${s.merged} ${m} · ${s.skipped} skipped · ${s.failed} failed`;
}

/** Reasons tally -> sorted [code, count, text] for the breakdown list. */
export function reasonBreakdown(reasons: Record<string, number>): Array<{ code: string; count: number; text: string }> {
  return Object.entries(reasons)
    .sort((a, b) => b[1] - a[1])
    .map(([code, count]) => ({ code, count, text: reasonText(code) }));
}
